import type { AskAlphaListWidgetModelEventBus, AskAlphaListWidgetModel } from '.';
import type { AskAlphaListWidgetAdapter } from '../AskAlphaListWidgetAdapter';
import type { ChannelSlice } from '../ChannelSlice';
import type { ColumnOptionsSlice } from '../ColumnOptionsSlice';
import type { FastIdeasSelector } from '../FastIdeasSelector';
import type { ResultsAreaSlice } from '../ResultsAreaSlice';
import type { SlowIdeasSelector } from '../SlowIdeasSelector';
import type { StickyFilterSlice } from '../StickyFilterSlice';
import type { TableModel } from '../TableModel';
import { makeHeaderElements } from '../WidgetModel';
import { COMMON_MENU_HEADER_ELEMENTS, STANDALONE_COMMON_MENU_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { computed, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { Idea } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { ColumnTemplate, KnownColumnTemplateKey } from '@thinkalpha/ui-types/contracts/column-template';
import { t } from 'i18next';
import { optional } from 'inversify';
import { createStructuredSelector } from 'reselect';
import type { TableStatus } from 'src/components/table-view/model';
import type { IdeaDTO, IdeaPlanDTO } from 'src/dtos/Idea';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import { ReactiveInjectable, reacts, inject, injectable } from 'src/features/ioc';
import { getValidationColumnDisplayName } from 'src/features/table/lib/validationColumns';
import type { ClientSortModel } from 'src/features/table/types';
import { scrollingFastIdeaDwellTimeConfig } from 'src/lib/featureFlags/flags/scrollingFastIdeaDwellTimeConfig';
import { useWidgetTabIdForUserViewKey } from 'src/lib/featureFlags/flags/useWidgetTabIdForUserViewKey';
import { exportTableToCSV } from 'src/lib/util/exportTableCSV';
import { getColumnTemplateByIdQuery, getDefaultColumnTemplateQuery } from 'src/queries/columnTemplates';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { RootState } from 'src/store';
import type { StrategyWidgetViewModel } from 'src/store/types';
import type { FetchedEntityType } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import { v4 } from 'uuid';

const makeSelectWidgetData = (tabId: string) =>
    createStructuredSelector({
        tab: (state: RootState) => state.tab.byId[tabId],
    });

const COLUMN_TEMPLATE_KEY: KnownColumnTemplateKey = 'ticker' as const;

/** Header element types hidden on the Alpha Scans standalone page. */
export const ALPHA_SCANS_HIDDEN_HEADER_TYPES = new Set([
    'stickyFilter',
    'columnTemplateMenu',
    'settingsMenu',
    'ideasMenu',
]);

/**
 * Projects the applied fast IdeaDTOs into the shape the ideas menu reads from
 * `widget::selectedFastIdeas`. Carrying `name` here lets the menu trigger label
 * render synchronously from the same source as the widget name.
 */
export function selectedFastIdeasFromFastIdeas(
    fastIdeas: readonly IdeaDTO<IdeaPlanDTO>[],
): { type: 'idea'; id: string; name?: string }[] {
    return fastIdeas.map((idea) => ({
        type: 'idea' as const,
        id: idea.idea.id as string,
        name: idea.name ?? undefined,
    }));
}

type SelectedFastIdea = { type: 'idea' | 'universe'; id: string; name?: string };

/**
 * `#fastIdeas` is the single source of truth for both the widget title (via
 * `widgetNameEffect`) and the menu trigger label. Earlier revisions paired this
 * with a fallback to the FastIdeasSelector's persisted-IDs signal to cover the
 * cold-reload window before `useQueries` resolves the IdeaDTOs, but the two
 * signals are written by independent paths and any divergence leaked into the
 * menu — most visibly on deselect-to-zero, where `#fastIdeas` cleared but the
 * fallback rendered the prior ID. Driving the menu off `#fastIdeas` only makes
 * title/menu drift impossible by construction; cold reload briefly shows the
 * unselected label, matching the title's existing tab-name flicker.
 */
export function selectedFastIdeasOverride(
    fastIdeasSignal: ReadonlySignal<readonly IdeaDTO<IdeaPlanDTO>[]>,
): ReadonlySignal<SelectedFastIdea[]> {
    return computed(() => selectedFastIdeasFromFastIdeas(fastIdeasSignal.value));
}

/**
 * Resolves a slow-idea selection payload to the universeId that should be persisted
 * onto the adapter. The Results widget runs with `syncFromUniverseId: false` so the
 * SlowIdeasSelector does not write the adapter automatically; ResultsAreaSlice's
 * universe-resolved loading subscribes to `adapter.universeId` to refresh the table.
 */
export function slowIdeaSelectionToUniverseId(ideas: readonly (IdeaDTO | { universeId: string })[]): string | null {
    const idea = ideas[0];
    if (!idea) return null;
    if ('universeId' in idea) return idea.universeId;
    return (idea as IdeaDTO<IdeaPlanDTO>).idea.id ?? null;
}

@injectable()
export class AskAlphaListWidgetModelImpl extends ReactiveInjectable implements AskAlphaListWidgetModel {
    // Private fields for selectors/slices (must be declared before eventBus which uses them)
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;
    #fastIdeasSelector: FastIdeasSelector;
    #slowIdeasSelector: SlowIdeasSelector;
    #stickyFilterSlice: StickyFilterSlice;
    #selectedColumnTemplate: Signal<ColumnTemplate | string | null> = signal(null);
    #columnTemplateResolveVersion = 0;
    #columnTemplateType: Signal<FetchedEntityType> = signal('default');
    #ideasInputSignal: Signal<(Idea | string)[] | null> = signal(null);
    #universeIdSignal: Signal<string | null> = signal(null);
    #isColumnTemplateLibraryOpen: Signal<boolean> = signal(false);
    #fastIdeas: Signal<IdeaDTO<IdeaPlanDTO>[]> = signal([]);
    // Signal for widget name override — shows idea name when exactly 1 fast idea is selected
    #widgetName: Signal<string> = signal('');
    #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    #gridSettingsDialogTarget: Signal<GridSettingsNavigationTarget | null> = signal(null);

    #isValidationModeEnabled: Signal<boolean> = signal(false);
    #isScrollingEnabled!: Signal<boolean>;
    #dwellTimeSeconds: Signal<number> = signal(0);
    #userViewKey: Signal<string | undefined> = signal(undefined);

    #tableConnectionStatusSignal!: Signal<{ status: TableStatus; progress: number | undefined; failureCount: number }>;

    headerElements!: ReturnType<typeof makeHeaderElements>;

    constructor(
        @inject('AskAlphaListWidgetAdapter') private adapter: AskAlphaListWidgetAdapter,
        @inject('WidgetDataModel') @reacts private widgetData: ReactBindings['WidgetDataModel'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('ColumnOptionsSlice') private columnOptionsSlice: ColumnOptionsSlice,
        @inject('FastIdeasSelector') fastIdeasSelector: FastIdeasSelector,
        @inject('SlowIdeasSelector') slowIdeasSelector: SlowIdeasSelector,
        @inject('StickyFilterSlice') stickyFilterSlice: StickyFilterSlice,
        @inject('ResultsAreaSlice') private resultsAreaSlice: ResultsAreaSlice,
        @inject('SuggestionsService') private suggestionsService: ReactBindings['SuggestionsService'],
        @inject('WidgetLocation') private location: ReactBindings['WidgetLocation'],
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        // Store slices and selectors as private fields (must be assigned before building eventBus)
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#fastIdeasSelector = fastIdeasSelector;
        this.#slowIdeasSelector = slowIdeasSelector;
        this.#stickyFilterSlice = stickyFilterSlice;

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        // Build eventBus in constructor after slices/selectors are available
        this.eventBus = this.#buildEventBus();

        void this.#setup();
    }

    private get widget() {
        return this.widgetData.widget as StrategyWidgetViewModel;
    }

    async #setup() {
        const tabId = this.location.type === 'workspace' ? this.location.tabId : undefined;
        const baseElements = tabId
            ? COMMON_MENU_HEADER_ELEMENTS
            : STANDALONE_COMMON_MENU_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
        this.headerElements = makeHeaderElements<AskAlphaListWidgetModelEventBus>([
            ...baseElements,
            { type: 'stickyFilter', position: 40, priority: 70 },
            {
                key: 'ideasMenu-slow',
                type: 'ideasMenu',
                position: 53,
                priority: 60,
                ideaType: 'slow',
                ideaPlanTypes: ['universe', 'screener', 'watchlist'],
                selectType: 'single',
                showSelected: true,
                allowDelete: false,
            },
            {
                key: 'ideasMenu-fast',
                type: 'ideasMenu',
                position: 54,
                priority: 60,
                ideaType: 'fast',
                ideaPlanTypes: ['if-then', 'scanner', 'formula'],
                selectType: 'multi',
                showSelected: true,
                allowDelete: false,
            },
            { type: 'columnTemplateMenu', position: 100, priority: 10, columnTemplateKey: COLUMN_TEMPLATE_KEY },
            { type: 'settingsMenu', position: 102, priority: 50 },
            { type: 'tableRowCount', position: 110, priority: 30 },
            { type: 'connectionStatus', position: 111, priority: 40 },
            { type: 'exportCSVButton', position: 120, priority: 20 },
        ]);

        if (this.location.type === 'standalonePage' && this.location.page === 'alpha-scans') {
            this.headerElements = this.headerElements.filter((el) => !ALPHA_SCANS_HIDDEN_HEADER_TYPES.has(el.type));
        }

        // In workspace mode, init widgetData early so this.widget is available for the fast ideas lens
        if (tabId) {
            this.widgetData.init(tabId);
        }

        // Initialize slices with shared adapter (works for both workspace and standalone)
        this.#stickyFilterSlice.init(this.adapter);
        this.disposableStack.use(this.#stickyFilterSlice);

        this.columnOptionsSlice.init(this.adapter);
        this.disposableStack.use(this.columnOptionsSlice);

        // syncFromUniverseId: false prevents SlowIdeasSelector from blindly wrapping
        // adapter.universeId in a Universe DTO during inbound hydration; ResultsAreaSlice
        // does that resolution itself via resolveUniverseId. We still need outbound
        // persistence so picking an SMI updates adapter.universeId, which the
        // ResultsAreaSlice subscribes to in order to refresh the table query.
        this.#slowIdeasSelector.init(this.adapter, {
            syncFromUniverseId: false,
            onSlowIdeaSelected: (payload) => {
                this.adapter.persist({ universeId: slowIdeaSelectionToUniverseId(payload.ideas) });
            },
        });
        this.disposableStack.use(this.#slowIdeasSelector);

        // Initialize FastIdeasSelector with lens
        if (tabId) {
            // Workspace mode: read idea IDs from Redux widget state
            this.#fastIdeasSelector.init(this.adapter, {
                get: () => {
                    const widget = this.widget;
                    return (widget.ideas ?? []).filter((id): id is string => typeof id === 'string');
                },
                set: (ideaIds) => ({ ideas: ideaIds }),
                onIdeasSelected: (payload) => {
                    const ideas = payload.ideas.filter((idea): idea is IdeaDTO => 'idea' in idea);
                    this.#fastIdeas.value = ideas;
                },
            });
        } else {
            // Standalone mode: ideas are pushed externally via setFastIdeas()
            this.#fastIdeasSelector.init(this.adapter, {
                get: () => [],
                set: () => ({}),
                onIdeasSelected: (payload) => {
                    const ideas = payload.ideas.filter((idea): idea is IdeaDTO => 'idea' in idea);
                    this.#fastIdeas.value = ideas;
                },
            });
        }

        // Initialize ResultsAreaSlice
        // resolveUniverseId enables the universe→idea fallback: if the adapter's
        // universeId is not a real universe (e.g. it's a screener/idea ID from a
        // canned search), it will be fetched as an idea instead of 404-ing.
        this.resultsAreaSlice.init(this.adapter, {
            columnTemplateKey: COLUMN_TEMPLATE_KEY,
            tabId,
            ideaMode: 'standard',
            resolveUniverseId: true,
            fastIdeasSelector: this.#fastIdeasSelector,
            slowIdeasSelector: this.#slowIdeasSelector,
            validationModeSignal: this.#isValidationModeEnabled,
            dwellTimeSignal: this.#dwellTimeSeconds,
            userViewKeySignal: this.#userViewKey,
            onUserSelectedSymbol: (symbol) => this.adapter.userSelectedSymbol(symbol),
            stickyFilterEventBus: this.#stickyFilterSlice.eventBus,
            columnOptionsEventBus: this.columnOptionsSlice.eventBus,
            devtoolsName: 'AskAlphaList/results',
        });

        this.#tableConnectionStatusSignal = computed(() => {
            const table = this.resultsAreaSlice.table;
            return {
                status: table.connectionStatus.value,
                progress: table.progress.value,
                failureCount: table.failureCount.value,
            };
        });

        this.disposableStack.use(this.resultsAreaSlice);

        this.#isScrollingEnabled = signal(this.adapter.getState().scrolling);

        if (tabId) {
            // Workspace mode: init Redux-dependent slices and listeners
            const selectWidgetData = makeSelectWidgetData(tabId);

            this.#channelSlice.init(tabId);
            this.#widgetTabSlice.init(tabId);
            this.disposableStack.use(this.#channelSlice);
            this.disposableStack.use(this.#widgetTabSlice);

            this.#ideasInputSignal.value = this.widget.ideas ?? [];
            this.#universeIdSignal.value = this.widget.universeId;

            // Derive widget name from fast ideas: show idea name when exactly 1 is selected
            const widgetNameEffect = effect(() => {
                const fastIdeas = this.#fastIdeas.value;
                if (fastIdeas.length === 1 && fastIdeas[0].name) {
                    this.#widgetName.value = fastIdeas[0].name;
                } else {
                    this.#widgetName.value = this.#widgetTabSlice.eventBus.getSignal('widget::name').value;
                }
            });
            this.disposableStack.defer(widgetNameEffect);

            // Initialize column template from widget state
            await this.resolveColumnTemplate(this.widget.columnTemplate);

            // Listen for widget updates
            const unsubscribe = this.store
                .subscribeSelector((state) => {
                    const tabExists = state.tab.byId[tabId] !== undefined;
                    if (!tabExists) {
                        return null;
                    }
                    return selectWidgetData(state);
                })
                .handle(async (widgetData) => {
                    if (widgetData !== null) {
                        const widget = widgetData.tab.widget as StrategyWidgetViewModel;

                        this.#ideasInputSignal.value = widget.ideas ?? [];
                        this.#universeIdSignal.value = widget.universeId;
                        this.#isScrollingEnabled.value = widget.scrolling ?? false;

                        await this.resolveColumnTemplate(widget.columnTemplate ?? null);
                    }
                });

            this.disposableStack.defer(() => {
                unsubscribe();
            });
        } else {
            // Standalone mode: derive widget name from fast ideas
            const widgetNameEffect = effect(() => {
                const fastIdeas = this.#fastIdeas.value;
                const firstName = fastIdeas.length > 0 ? fastIdeas[0].name : null;
                this.#widgetName.value = firstName ?? t('widgets::results::name');
            });
            this.disposableStack.defer(widgetNameEffect);

            // Initialize column template from adapter state
            let persistedColumnTemplate = this.adapter.getState().columnTemplate;
            await this.resolveColumnTemplate(persistedColumnTemplate);

            // Listen for adapter state changes (e.g. async init loading default universe)
            const unsubscribeAdapter = this.adapter.subscribe((state) => {
                this.#universeIdSignal.value = state.universeId;
                this.#isScrollingEnabled.value = state.scrolling;
                if (state.columnTemplate !== persistedColumnTemplate) {
                    persistedColumnTemplate = state.columnTemplate;
                    void this.resolveColumnTemplate(persistedColumnTemplate);
                }
            });
            this.disposableStack.defer(unsubscribeAdapter);
        }

        // Subscribe to dwell time feature flag (global state, works in both modes)
        const dwellTimeUnsubscribe = this.store
            .subscribeSelector((state) => scrollingFastIdeaDwellTimeConfig.select(state))
            .handle((config) => {
                this.#dwellTimeSeconds.value = config.scrollingDwellTimeSeconds;
            });
        this.disposableStack.defer(dwellTimeUnsubscribe);

        // Subscribe to userViewKey feature flag
        const uniqueMountId = v4();
        const userViewKeyUnsubscribe = this.store
            .subscribeSelector((state) => useWidgetTabIdForUserViewKey.select(state))
            .handle((useTabId) => {
                this.#userViewKey.value = useTabId && tabId ? tabId : uniqueMountId;
            });
        this.disposableStack.defer(userViewKeyUnsubscribe);
    }

    /**
     * Resolves the column template from the widget state.
     * The widget.columnTemplate can be:
     * - null: fetch the default template for the key
     * - string: fetch the template by ID
     * - ColumnTemplate object: use it directly (concrete)
     */
    private async resolveColumnTemplate(incomingColumnTemplate: ColumnTemplate | string | null): Promise<void> {
        const resolveVersion = ++this.#columnTemplateResolveVersion;
        const key = COLUMN_TEMPLATE_KEY;

        if (incomingColumnTemplate === null) {
            // Fetch default template
            const queryOptions = getDefaultColumnTemplateQuery(key);
            const template = await this.queryClient.fetchQuery(queryOptions);
            if (resolveVersion !== this.#columnTemplateResolveVersion) return;
            const sanitizedTemplate = generateWorkableColumnTemplateGridSettings(template ?? null);
            this.#selectedColumnTemplate.value = sanitizedTemplate;
            this.#columnTemplateType.value = 'default';
        } else if (typeof incomingColumnTemplate === 'string') {
            // Fetch referenced template by ID
            const queryOptions = getColumnTemplateByIdQuery(incomingColumnTemplate);
            const template = await this.queryClient.fetchQuery(queryOptions);
            if (resolveVersion !== this.#columnTemplateResolveVersion) return;
            this.#selectedColumnTemplate.value = generateWorkableColumnTemplateGridSettings(template ?? null);
            this.#columnTemplateType.value = 'referenced';
        } else {
            // Use the concrete template directly
            this.#selectedColumnTemplate.value = generateWorkableColumnTemplateGridSettings(incomingColumnTemplate);
            this.#columnTemplateType.value = 'concrete';
        }
    }

    eventBus!: AskAlphaListWidgetModelEventBus;

    #buildEventBus(): AskAlphaListWidgetModelEventBus {
        return MergedEventBus.from(
            EventBusBuilder.create()
                // Events
                .addEvent('widget::selectColumnTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.templateId });
                })
                .addEvent('widget::setColumnTemplateLibraryOpen', (payload) => {
                    this.#isColumnTemplateLibraryOpen.value = payload.isOpen;
                })
                .addEvent('widget::updateConcreteTemplate', (payload) => {
                    this.adapter.persist({ columnTemplate: payload.columnTemplate });
                    this.#columnTemplateType.value = 'concrete';
                })
                .addEvent(
                    'widget::onColumnTemplateLibraryOpen',
                    (payload) => (this.#isColumnTemplateLibraryOpen.value = payload.isOpen),
                )
                .addEvent('widget::exportCSV', () => {
                    void this.#exportCSV();
                })
                .addEvent('widget::toggleScrolling', () =>
                    this.adapter.persist({ scrolling: !this.#isScrollingEnabled.value }),
                )
                .addEvent(
                    'widget::toggleValidationMode',
                    () => (this.#isValidationModeEnabled.value = !this.#isValidationModeEnabled.value),
                )
                // Signals
                .addSignal('widget::formattableGridSettings', () =>
                    computed(() => {
                        const columnTemplate = this.#selectedColumnTemplate.value;
                        if (!columnTemplate) {
                            return { settings: null, defaults: null };
                        }
                        const sanitized = generateWorkableColumnTemplateGridSettings(columnTemplate as ColumnTemplate);
                        return { settings: sanitized, defaults: sanitized };
                    }),
                )
                // Column template selection signals
                .addSignal('widget::selectedColumnTemplate', () => this.#selectedColumnTemplate)
                .addSignal('widget::isColumnTemplateLibraryOpen', () => this.#isColumnTemplateLibraryOpen)
                .addEvent('widget::openGridSettingsDialog', (payload) => {
                    this.#isGridSettingsDialogOpen.value = payload.isOpen;
                    this.#gridSettingsDialogTarget.value =
                        payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                            ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                            : null;
                })
                .addSignal('widget::isGridSettingsDialogOpen', () => this.#isGridSettingsDialogOpen)
                .addSignal('widget::gridSettingsDialogTarget', () => this.#gridSettingsDialogTarget)
                .addSignal('widget::connectionStatus', () => this.#tableConnectionStatusSignal)
                .addSignal('widget::tableRowCount', () => this.resultsAreaSlice.table.countSignal)
                .addSignal('widget::isValidationModeEnabled', () => this.#isValidationModeEnabled)
                .addSignal('widget::isScrollingEnabled', () => this.#isScrollingEnabled)
                // Account signals - stub for CommonWidgetSignals compatibility
                .addSignal('widget::selectedAccountIds', () => computed(() => [] as string[]))
                .addSignal('widget::selectedAccountId', () => computed(() => null))
                .addSignal('widget::isAccountSelectionValid', () => computed(() => true))
                .addSignal('widget::instrument', () => computed(() => ''))
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.resultsAreaSlice.table.refetch();
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus)
            .merge(
                // Override widget::name to show idea name instead of tab name
                EventBusBuilder.create()
                    .addSignal('widget::name', () => this.#widgetName)
                    .build(),
            )
            .merge(this.#stickyFilterSlice.eventBus)
            .merge(this.#fastIdeasSelector.eventBus)
            .merge(this.#slowIdeasSelector.eventBus)
            .merge(
                EventBusBuilder.create()
                    .addSignal('widget::selectedFastIdeas', () => selectedFastIdeasOverride(this.#fastIdeas))
                    .build(),
            )
            .merge(this.columnOptionsSlice.eventBus)
            .merge(this.resultsAreaSlice.eventBus);
    }

    userSelectedSymbol(symbol: string) {
        this.adapter.userSelectedSymbol(symbol);
    }

    setFastIdeas(fastIdeas: IdeaDTO<IdeaPlanDTO>[]): void {
        this.#fastIdeas.value = fastIdeas;
        this.resultsAreaSlice.setFastIdeas(fastIdeas);
        // Update widget name from idea name
        const firstName = fastIdeas.length > 0 ? fastIdeas[0].name : null;
        this.#widgetName.value = firstName ?? t('widgets::results::name');
    }

    setSlowIdea(slowIdea: IdeaDTO<IdeaPlanDTO> | null | undefined): void {
        this.#slowIdeasSelector.setSlowIdea(slowIdea);
    }

    get validationColumns(): OutputColumnWithMetadata[] {
        return this.resultsAreaSlice.eventBus.getSignal('widget::validationColumns').value;
    }

    get isValidationModeEnabled(): Signal<boolean> {
        return this.#isValidationModeEnabled;
    }

    get isScrollingEnabled(): Signal<boolean> {
        return this.#isScrollingEnabled;
    }

    get table(): TableModel {
        return this.resultsAreaSlice.table;
    }

    setClientSortModel(sortModel: ClientSortModel): void {
        this.resultsAreaSlice.eventBus.emit('widget::setClientSortModel', { sortModel });
    }

    /**
     * Exports the current table data to CSV.
     * Handles the entire export process including data fetching, column name mapping,
     * CSV generation, and file download.
     */
    async #exportCSV(): Promise<void> {
        const columnTemplate = this.#selectedColumnTemplate.value;
        if (columnTemplate == null) {
            return;
        }

        const validationColumns = this.resultsAreaSlice.eventBus.getSignal('widget::validationColumns').value;

        const columnNames = [
            ...(columnTemplate as ColumnTemplate).columns.map((col) => ({
                id: col.id,
                name: col.name,
            })),
            ...validationColumns.map((column) => ({
                id: column.id,
                name: getValidationColumnDisplayName(column),
            })),
        ].reduce(
            (names, { id, name }) => {
                names[id] = name;
                return names;
            },
            {} as Record<string, string>,
        );

        await exportTableToCSV(this.resultsAreaSlice.table, columnNames, t);
    }
}
